/**
 * Asking Maestro for a connector Symphony cannot connect: a coming-soon card on the Connectors screen, or the
 * free-text card at the end of it. Both open a new chat with a visible seed and a hidden turn `context`, and
 * Maestro reports the request to the team from inside that chat, so the report carries its conversation. The
 * count of taps per app is the `coming_soon_<toolkit>` click event, which fires whether or not a report follows.
 *
 * The context sentences and the `comingSoon[]` reading rule are byte-identical on iOS
 * (`ConnectorRequests.swift`) and Android (`ConnectorRequests.kt`); `test/connector-requests.test.ts` pins the
 * strings. The contexts name no harness tool, because an installed app keeps sending them after a tool is
 * renamed. Import-free so the test reads it without the app's dependency graph.
 */

/** One coming-soon app as the connectors status serves it (`comingSoon[]`, next to `connectors[]`). */
export interface ComingSoonConnector {
  toolkit: string;
  name: string;
  description: string;
  logo?: string;
}

export function comingSoonContext(name: string): string {
  return (
    `The user tapped ${name} on the Connectors screen. It is listed there with the apps to connect, but Symphony's catalog can't connect it yet, ` +
    `so skip a catalog connect, which can only fail. If they already have ${name} connected some other way, tell them it is ready. ` +
    `Otherwise see whether ${name} hosts an MCP server of its own and, if it does, offer it to them. ` +
    `Either way the tap is the request itself, so report it to the team as a feature request for ${name}, saying whether you found that server. ` +
    `If you found none, tell them ${name} is coming soon and that you've passed their request to the team, ` +
    `and if an app they can connect now serves the same goal, offer it.`
  );
}

export const CONNECTOR_REQUEST_CONTEXT =
  "The user typed this into the box at the end of the Connectors screen that asks for a connector they couldn't find there. " +
  'First check whether it can be connected after all, in your connector lists and then with one connect attempt by that name, ' +
  'because the list may name it differently; connect it if so. ' +
  "Only if it isn't connectable: report it to the team as a feature request in their words, saying whether the app hosts an MCP server of its own, " +
  "and tell them you did; then offer that server if it does, or else tell them it isn't available yet and offer the closest app they can connect for the same goal.";

/** The server's `comingSoon[]`, minus any entry a card could not be drawn from. */
export function readComingSoon(value: unknown): ComingSoonConnector[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const { toolkit, name, description, logo } = (entry ?? {}) as Record<string, unknown>;
    if (typeof toolkit !== 'string' || !toolkit.trim() || typeof name !== 'string' || !name.trim()) return [];
    return [
      { toolkit, name, description: typeof description === 'string' ? description : '', ...(typeof logo === 'string' && logo ? { logo } : {}) },
    ];
  });
}
